import {
  Box,
  Button,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  pn,
  SearchBar,
  Stack,
  Tab,
  TabContext,
  Typography,
  useElementBreakpoints,
  SCHOOL_YEAR_ACTIVE_ID,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import Tabs from "@mui/material/Tabs";
import {
  Permission,
  PermissionAction,
  Student,
  StudentEditResource,
  StudentListDto,
  StudentListFilter,
  StudentServicePartialStudent,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
  StudentViewResource,
  StudentEnrollment as StudentEnrollmentModel,
  TenantInfo,
} from "generated/openApi";
import { isAuthorized } from "services";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { useInitListFilter } from "hooks";
import { BasicInfo } from "pages/students/students/StudentPage/StudentProfile/BasicInfo";
import { createEntity } from "utils";
import { Enrollments } from "pages/students/students/StudentPage/StudentProfile/Enrollments";
import { StudentEnrollment } from "pages/students/students/StudentCreatePage/StudentEnrollment";

export type StudentLinkTypeTab = "Existing Student" | "New Student";
const tabs: StudentLinkTypeTab[] = ["Existing Student", "New Student"];

export const StudentLinkPageBody = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  newStudent: Student | undefined;
  existingStudent: StudentListDto | undefined;
  newStudentEditResource: StudentEditResource;
  newStudentViewResource: StudentViewResource;
  currentTab: StudentLinkTypeTab;
  setCurrentTab: (value: StudentLinkTypeTab) => void;
  setNewStudent: (value: Student) => void;
  setExistingStudent: (value: StudentListDto) => void;
  partialStudent: StudentServicePartialStudent;
  viewResource: StudentServiceTemplateViewResource;
  editResource: StudentServiceTemplateEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];

  const [customId1Focused, setCustomId1Focused] = useState(false);
  const [existingStudents, setExistingStudents] = useState<StudentListDto[]>([]);
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();

  const tabList: StudentLinkTypeTab[] = ["Existing Student", "New Student"];

  const { listFilter, setListFilter } = useInitListFilter<StudentListFilter>({
    sortObjects: [{ property: "fullName", isDesc: false }],
    studentFilter: {
      schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    },
  });

  useEffect(() => {
    getExistingStudents();
  }, [listFilter]);

  const getExistingStudents = () => {
    ApiService.students.getList(listFilter).then((response) => {
      setExistingStudents(response);
    });
  };

  const applySearch = (event: any) => {
    if (event.type != "click") {
      setListFilter(
        produce(listFilter, (draft) => {
          draft.searchString = event?.target?.value!;
        })
      );
    }
  };

  return (
    <Stack spacing="lg">
      <Card ref={breakpointsRef}>
        <Stack spacing="xl">
          <Grid container spacing="sm">
            <Grid item xs={12}>
              <TabContext value={props.currentTab}>
                <Tabs
                  onChange={(e, value) => props.setCurrentTab(value)}
                  value={props.currentTab}
                  variant="standard"
                  scrollButtons
                  allowScrollButtonsMobile
                >
                  {tabList.map((tab) => (
                    <Tab key={tab} label={tab} value={tab} />
                  ))}
                </Tabs>
                <Box hidden={props.currentTab !== "Existing Student"}>
                  <Grid container spacing="sm" style={{ paddingTop: 15 }}>
                    <Grid item xs={4}>
                      <Input disabled label="Student Name" value={props.partialStudent.studentName}></Input>
                    </Grid>
                    <Grid item xs={4}>
                      <DatePicker disabled label="Date of Birth" value={props.partialStudent.dateOfBirth} />
                    </Grid>

                    <GridBreak />

                    <Grid item xs={4}>
                      <Typography>Links to:</Typography>
                    </Grid>

                    <GridBreak />

                    <Grid item xs={4}>
                      <Select
                        label="Existing Student"
                        value={props.existingStudent}
                        options={existingStudents}
                        getOptionLabel={(option) =>
                          `${option.fullName || ""} (${option.customId1}/${option.currentSchool})`
                        }
                        onInputChange={(e) => applySearch(e)}
                        onChange={(e, value) => props.setExistingStudent(value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box hidden={props.currentTab !== "New Student" || !props.newStudent} style={{ paddingTop: 15 }}>
                  <Grid container spacing="sm">
                    <Grid item xs={12}>
                      <BasicInfo
                        student={props.newStudent || createEntity<Student>({})}
                        tenantTextingInfo={undefined}
                        setStudent={props.setNewStudent}
                        crudMode={props.crudMode}
                        // unsavedChanges={unsavedChanges}
                        // save={save}
                        viewResource={props.newStudentViewResource}
                        editResource={props.newStudentEditResource}
                        validationErrors={props.validationErrors}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StudentEnrollment
                        enrollment={props.newStudent?.creatingFirstEnrollment || ({} as StudentEnrollmentModel)}
                        setEnrollment={(enrollment) =>
                          props.setNewStudent(
                            produce(props.newStudent!, (draft) => {
                              draft.creatingFirstEnrollment = enrollment;
                            })
                          )
                        }
                        validationErrors={props.validationErrors[pn<Student>("creatingFirstEnrollment")] || {}}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </TabContext>
            </Grid>
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
};
